import {DeepReadonly, Writable} from 'ts-essentials'
import {logger, ObjUtil, Util, UUID} from 'yougile-util'
import {ConnectionsJSON} from './connections'
import {DataObjectId, DataObjectJSON} from './data-object'

export type JSONLikeValue = any
export interface JSONLike {
  [key: string]: JSONLikeValue
}

const canBeArrayIndex = (x: any): boolean => {
  if (+x === 0) {
    return true
  }
  return +x > 0 && parseInt(+x + '') === +x
}

/**
 * Values that are equivalent to null in JSON.
 */
const isNully = (val: JSONLike | undefined) => {
  return (
    val === null ||
    typeof val === 'undefined' ||
    (typeof val === 'number' && !isFinite(val))
  )
}

const removeNullsNaNsAndInfinity = (
  obj: JSONLike | undefined
): JSONLike | undefined => {
  if (isNully(obj)) {
    return undefined
  }
  if (typeof obj === 'object') {
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) {
        continue
      }
      const next = obj[key]
      if (isNully(next)) {
        delete obj[key]
      } else if (typeof next === 'object') {
        obj[key] = removeNullsNaNsAndInfinity(next)
      }
    }
    return obj
  } else if (
    typeof obj === 'boolean' ||
    typeof obj === 'number' ||
    typeof obj === 'string'
  ) {
    return obj as any
  } else {
    logger.warn('Unsupported value', typeof obj, obj)
    return undefined
  }
}

const convertToArrayIfPossible = (obj: JSONLike | undefined) => {
  const keys = Object.keys(obj ?? {})
  const keyLen = keys.length
  if (!obj || typeof obj !== 'object' || keyLen === 0 || obj instanceof Array) {
    return obj
  }

  if (
    keys.every((k: JSONLikeValue) => {
      const kInt = +k
      return kInt === 0 || (kInt > 0 && kInt < keyLen)
    })
  ) {
    const res: JSONLikeValue[] = []
    for (const k of keys) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) {
        continue
      }
      res[+k] = obj[k]
    }
    return res
  } else {
    return obj
  }
}

const walk = (
  obj: JSONLike | undefined,
  path: Array<string | number>,
  cb: (path: Array<string | number>, val: JSONLikeValue) => boolean
): void => {
  if (typeof obj === 'undefined' || obj === null) {
    throw Error('Object must be defined')
  }
  const step = (key: string | number) => {
    key = obj instanceof Array ? +key : '' + key
    const proceed = cb([...path, key], obj[key])
    const o = obj[key]
    if (proceed && !!o && typeof o === 'object' && Object.keys(o).length > 0) {
      if (obj instanceof Array && !+key && +key !== 0) {
        throw new Error('Tried to add incorrect index to an array')
      }
      walk(o, [...path, key], cb)
    }
  }
  if (obj instanceof Array) {
    for (let i = obj.length - 1; i >= 0; i--) {
      if (!Object.prototype.hasOwnProperty.call(obj, i)) {
        continue
      }
      step(i)
    }
  } else {
    for (const key in obj) {
      if (
        !Object.prototype.hasOwnProperty.call(obj, key) ||
        key === '$$hashKey'
      ) {
        continue
      }
      step(key)
    }
  }
}

const getByPath = (
  o: JSONLike,
  cPath: Array<string | number>
): JSONLikeValue => {
  const path = [...cPath]
  if (!path || path.length === 0) {
    throw new Error('Incorrect path supplied')
  } else {
    let currObj = o
    let currKey = path.shift()
    while (typeof currKey !== 'undefined') {
      if (!currObj) {
        return undefined
      }
      if (
        currKey !== null &&
        Object.prototype.hasOwnProperty.call(currObj, currKey + '')
      ) {
        currObj = currObj[currKey]
      } else {
        return undefined
      }
      currKey = path.shift()
    }
    return currObj
  }
}

const setByPath = (
  o: JSONLike,
  cPath: Array<string | number>,
  val: JSONLikeValue,
  preserveArrays: boolean
) => {
  const path: Array<string | number> = [...cPath]
  if (!path || path.length === 0) {
    throw new Error('Incorrect path supplied')
  } else {
    let currObj = o
    const lastKey = path.pop()
    let currKey = path.shift()
    while (typeof currKey !== 'undefined') {
      let nextKey: typeof currKey | undefined = path[0]
      if (typeof nextKey === 'undefined') {
        nextKey = lastKey
      }
      if (
        !Object.prototype.hasOwnProperty.call(currObj, '' + currKey) ||
        typeof currObj[currKey] !== 'object'
      ) {
        if (typeof val === 'undefined') {
          return
        }
        nextKey = path[0]
        if (typeof nextKey === 'undefined') {
          nextKey = lastKey
        }
        currObj[currKey] = canBeArrayIndex(nextKey) && preserveArrays ? [] : {}
      }
      currObj = currObj[currKey]
      currKey = path.shift()
    }
    if (typeof lastKey !== 'undefined') {
      if (typeof val === 'undefined') {
        if (currObj instanceof Array && canBeArrayIndex(lastKey)) {
          currObj.splice(+lastKey, 1)
        } else {
          delete (currObj as JSONLike)[lastKey]
        }
      } else {
        currObj[lastKey] = val
      }
    }
  }
}

/**
 * Returns true if it is more optimal to reassign array at once
 * than to modify its elements.
 */
const betterToReassign = (a1: any[], a2: any[]): boolean => {
  for (const v of a1) {
    if (typeof v === 'object') {
      return false
    }
  }
  for (const v of a2) {
    if (typeof v === 'object') {
      return false
    }
  }
  if (a1.length === 0 && a2.length === 0) {
    return false
  }
  if (a1.length === 0 || a2.length === 0) {
    return true
  }
  let mismatches = 0
  a1.forEach((v, ix) => {
    if (v !== a2[ix]) {
      mismatches++
    }
  })
  mismatches += Math.abs(a1.length - a2.length)
  return mismatches >= a1.length
}

export type JSONKeyPath = Array<string | number>

const addPlusMinusFromConnectionsChange = (
  oldConn: ConnectionsJSON,
  newConn: ConnectionsJSON,
  changedConnectionToIds: DataObjectId[],
  plus: Array<[JSONKeyPath, JSONLikeValue]>,
  minus: Array<[JSONKeyPath, JSONLikeValue]>
): void => {
  changedConnectionToIds.forEach(id => {
    if (oldConn.to[id] && !newConn.to[id]) {
      minus.push([['to', id], '---'])
    } else if (!oldConn.to[id] && newConn.to[id]) {
      plus.push([['to', id], newConn.to[id]])
    }
    if (oldConn.modLists[id] && !newConn.modLists[id]) {
      minus.push([['modLists', id], '---'])
    } else if (!oldConn.modLists[id] && newConn.modLists[id]) {
      plus.push([['modLists', id], newConn.modLists[id]])
    } else if (oldConn.modLists[id] && newConn.modLists[id]) {
      if (
        !ObjUtil.deepEqual(oldConn.modLists[id], newConn.modLists[id], false)
      ) {
        plus.push([['modLists', id], newConn.modLists[id]])
      }
    }
  })
}

/**
 * Represents changes in one DataObject between .revision and the
 * given state.
 */
export class Diff {
  public revision = 0
  public isNew = false
  public newObj?: DataObjectJSON
  public plus: Array<[JSONKeyPath, JSONLikeValue]> = []
  public minus: Array<[JSONKeyPath, JSONLikeValue]> = []

  public static isEmpty(diff: Diff): boolean {
    return !diff.isNew && diff.plus.length === 0 && diff.minus.length === 0
  }

  public static calculate(
    jsonOld: DataObjectJSON | undefined,
    jsonNew: DataObjectJSON,
    changedConnectionToIds?: DataObjectId[]
  ): Diff {
    if (typeof jsonOld === 'undefined') {
      const result = new Diff()
      result.revision = jsonNew.revision || 1
      result.isNew = true
      result.newObj = jsonNew
      return result
    }
    if (typeof jsonNew === 'undefined') {
      logger.error('Object deletion is disallowed')
      return new Diff()
    }
    if (!jsonOld) {
      logger.log('Empty jsonOld but not undefined!', jsonOld)
    }
    if (jsonOld.id !== jsonNew.id) {
      logger.error(
        'Calculating diff between objects with different ids is forbidden'
      )
      return new Diff()
    }

    if (
      ((jsonOld.revision || 1) < 2 && (jsonNew.revision || 1) < 2) ||
      (jsonOld.revision &&
        jsonNew.revision &&
        jsonOld.revision >= 0 &&
        jsonNew.revision >= 0 &&
        jsonOld.revision === jsonNew.revision)
    ) {
      const result = new Diff()
      result.revision = jsonOld.revision || 1

      const plus: Array<[JSONKeyPath, JSONLikeValue]> = []
      const minus: Array<[JSONKeyPath, JSONLikeValue]> = []
      if (changedConnectionToIds) {
        addPlusMinusFromConnectionsChange(
          jsonOld as ConnectionsJSON,
          jsonNew as ConnectionsJSON,
          changedConnectionToIds,
          plus,
          minus
        )
      }

      // calc plus
      walk(
        convertToArrayIfPossible(removeNullsNaNsAndInfinity(jsonNew)),
        [],
        (path: (string | number)[], val: JSONLikeValue) => {
          if (
            changedConnectionToIds &&
            (path[0] === 'to' || path[0] === 'modLists')
          ) {
            return false
          }
          const oldVal = getByPath(jsonOld, path)
          if (val === null || typeof val === 'undefined') {
            return false
          }
          if (typeof val !== 'object') {
            if (val !== oldVal) {
              plus.push([path, val])
            }
            return false
          } else {
            if (val instanceof Array && oldVal instanceof Array) {
              if (betterToReassign(val, oldVal)) {
                plus.push([path, val])
                return false
              }
            }
            if (typeof oldVal !== 'object') {
              plus.push([path, val])
              return false
            } else {
              if (val instanceof Array !== oldVal instanceof Array) {
                plus.push([path, val])
                return false
              } else {
                return true
              }
            }
          }
        }
      )
      result.plus = plus

      // calc minus
      walk(
        convertToArrayIfPossible(removeNullsNaNsAndInfinity(jsonOld)),
        [],
        (path: (string | number)[], oldVal: JSONLikeValue) => {
          if (
            changedConnectionToIds &&
            (path[0] === 'to' || path[0] === 'modLists')
          ) {
            return false
          }
          const val = getByPath(jsonNew, path)
          if (typeof getByPath(jsonNew, path) === 'undefined') {
            minus.push([path, '---'])
            return false
          }
          if (val instanceof Array && oldVal instanceof Array) {
            if (betterToReassign(val, oldVal)) {
              return false
            }
          }
          return true
        }
      )
      result.minus = minus

      return result
    } else {
      throw new Error(
        'Wrong revision ' + jsonOld.revision + ' ' + jsonNew.revision
      )
    }
  }

  public static merge(diff1: Diff, diff2: Diff): Diff {
    if (diff1.isNew && diff2.isNew) {
      if (diff1.newObj?.id === diff2.newObj?.id) {
        logger.warn('Merging two object creations', diff1.newObj?.id)
        return diff1
      } else {
        logger.warn(
          'Merging two object creations with different ids!',
          diff1,
          diff2
        )
        return diff1
      }
    }
    if (diff1.isNew) {
      const res = Util.clone(diff1)
      const newObj = diff1.newObj as unknown as DeepReadonly<DataObjectJSON>
      res.newObj = Diff.apply(diff2, newObj) as DataObjectJSON
      return res
    }
    if (diff2.isNew) {
      const res = Util.clone(diff2)
      const newObj = diff2.newObj as unknown as DeepReadonly<DataObjectJSON>
      res.newObj = Diff.apply(diff1, newObj) as DataObjectJSON
      return res
    }
    const result = new Diff()
    result.revision = Math.max(diff1.revision, diff2.revision)

    result.minus = Util.clone([...diff2.minus])
    result.plus = Util.clone([...diff2.plus])

    const alreadySetPath = new Set<string>()
    const alreadySetMinus = new Set<string>()
    for (const p of result.plus) {
      if (p[0]) {
        alreadySetPath.add(p[0].join(','))
      }
    }
    for (const m of result.minus) {
      if (m[0]) {
        alreadySetPath.add(m[0].join(','))
        alreadySetMinus.add(m[0].join(','))
      }
    }

    for (const p of diff1.plus) {
      if (p[0]) {
        const k = p[0].join(',')
        if (!alreadySetPath.has(k)) {
          const subpath: (string | number)[] = []
          let isRemoved = false
          for (const s of p[0]) {
            subpath.push(s)
            if (alreadySetMinus.has(subpath.join(','))) {
              isRemoved = true
            }
          }
          if (!isRemoved) {
            result.plus.push(Util.clone(p))
          }
        }
      }
    }

    for (const m of diff1.minus) {
      if (m[0]) {
        const k = m[0].join(',')
        if (!alreadySetPath.has(k)) {
          result.minus.push(Util.clone(m))
        }
      }
    }

    return result
  }

  /**
   * Should return cloned object (to prevent mutability).
   */
  public static apply<T extends DeepReadonly<DataObjectJSON>>(
    diff: Diff,
    json: T | undefined,
    allowedPaths?: {
      [key: string]: true
    }
  ): Writable<T> {
    if (json === null || typeof json === 'undefined') {
      if (diff.isNew) {
        // return cloned for immutability
        return Util.clone(diff.newObj as unknown as T)
      } else {
        logger.warn('Partial diff', JSON.stringify(diff))
        return json as unknown as T
      }
    }
    const cloned = Util.clone(json)

    if (diff.revision >= 0) {
      if ((diff.revision || 1) !== (+(cloned.revision ?? 1) || 1)) {
        logger.log('revision miss', diff, cloned, new Error().stack)
      }
      if (diff.isNew) {
        logger.error(
          'Creating already existent object',
          diff.newObj && ((diff.newObj as any).title || diff.newObj.id)
        )
        if (diff.newObj?.id === cloned.id) {
          return cloned
        } else {
          logger.log('Applying create with incorrect id')
          return cloned
        }
      }
      diff.minus.forEach((item: [JSONKeyPath, JSONLikeValue]) => {
        if (allowedPaths && !allowedPaths[item[0].join('.')]) {
          logger.log('Skipping path', item[0].join('.'))
          return
        }
        setByPath(cloned, item[0], undefined, true)
        // const currPath = item[0]
        // if (currPath.includes('data') && currPath.includes('mirrors')) {
        //   logger.error('Mirror can be deleted')
        //   // eslint-disable-next-line
        //   console.trace()
        // }
        // if (currPath.includes('data') && currPath.length === 1) {
        //   logger.error('Hub.data can be deleted')
        //   logger.error(JSON.stringify(json, null, ' '))
        //   logger.error(JSON.stringify(diff, null, ' '))
        //   try {
        //     throw new Error('Cannot delete data')
        //   } catch (e) {
        //     logger.error(e)
        //   }
        // }
      })
      diff.plus.forEach((item: [JSONKeyPath, JSONLikeValue]) => {
        if (allowedPaths && !allowedPaths[item[0].join('.')]) {
          logger.log('Skipping path', item[0].join('.'))
          return
        }
        setByPath(cloned, item[0], item[1], true)
      })

      return cloned
    } else {
      logger.error('Incorrect revision')
      return cloned
    }
  }

  /**
   * Can be used in situations when object is reassined to the computed
   * new object, for example
   * obj = Diff.apply(diff, obj)
   * can be converted to
   * obj = Diff.applyInPlace(diff, obj)
   *
   * It clones makes cloning only for new objects creation
   */
  public static applyInPlace<T extends DeepReadonly<DataObjectJSON>>(
    diff: Diff,
    json: T,
    allowedPaths?: {
      [key: string]: true
    }
  ): {result: Writable<T>; changed: boolean} {
    if (json === null || typeof json === 'undefined') {
      if (diff.isNew) {
        // return cloned for immutability
        return {
          result: Util.clone(diff.newObj as unknown as T),
          changed: true
        }
      } else {
        logger.warn('Partial diff (applyInPlace)', JSON.stringify(diff))
        return {result: json, changed: false}
      }
    }
    if (diff.revision >= 0) {
      if ((diff.revision || 1) !== (+(json.revision ?? 1) || 1)) {
        logger.error('revision miss', diff, json)
        logger.warn(
          'revision miss (applyInPlace)',
          new Error().stack,
          JSON.stringify(diff),
          JSON.stringify(json)
        )
      }
      if (diff.isNew) {
        logger.error(
          'Creating already existent object',
          diff.newObj && ((diff.newObj as any).title || diff.newObj.id)
        )
        if (diff.newObj?.id === json.id) {
          return {result: json, changed: true}
        } else {
          logger.log('Applying create with incorrect id')
          return {result: json, changed: true}
        }
      }
      let changed = false
      diff.minus.forEach((item: [JSONKeyPath, JSONLikeValue]) => {
        if (allowedPaths && !allowedPaths[item[0].join('.')]) {
          logger.log('Skipping path', item[0].join('.'))
          return
        }
        changed = true
        setByPath(json, item[0], undefined, true)
      })
      diff.plus.forEach((item: [JSONKeyPath, JSONLikeValue]) => {
        if (allowedPaths && !allowedPaths[item[0].join('.')]) {
          logger.log('Skipping path', item[0].join('.'))
          return
        }
        changed = true
        setByPath(json, item[0], item[1], true)
      })
      return {result: json, changed}
    } else {
      logger.error('Incorrect revision')
      return {result: json, changed: false}
    }
  }
}

export interface BranchJSON {
  companyId: DataObjectId
  fromRevision: number
  toRevision?: number
  id: UUID
  changes: {[id: string]: Diff}
}

/**
 * Branch represents all changes in some sandbox (identified by .companyId)
 * between .revision and the given state.
 */
export class Branch {
  public companyId: DataObjectId
  public fromRevision = 1
  /* When branch is calculated on client side “toRevision” must be undefined
     because only server can provide a new revision number */
  public toRevision?: number
  public id: UUID // branch unique id
  public changes: {[id: string]: Diff} = {} // dataObject.id -> diff
  public attempts = 0 // number of sending attempts
  public approved = false

  constructor(companyId: DataObjectId) {
    this.id = Util.UUID()
    this.companyId = companyId
  }

  public toJSON(): BranchJSON {
    return {
      companyId: this.companyId,
      fromRevision: this.fromRevision || 1,
      toRevision: this.toRevision,
      id: this.id,
      changes: Util.clone(this.changes)
    }
  }

  public static fromJSON(json: BranchJSON): Branch {
    if (!json) {
      return json as any
    }
    const b = new Branch(json.companyId)
    b.fromRevision = json.fromRevision
    b.toRevision = json.toRevision
    b.id = json.id
    b.changes = Util.clone(json.changes)
    return b
  }

  public apply(
    companyId: DataObjectId,
    id: DataObjectId,
    json: DataObjectJSON
  ): DataObjectJSON {
    if (this.companyId !== companyId) {
      logger.error('Incorrect branch companyId')
      return json
    }
    if (this.changes[id]) {
      return Diff.apply(
        this.changes[id],
        json as unknown as DeepReadonly<DataObjectJSON>
      ) as DataObjectJSON
    } else {
      return Util.clone(json)
    }
  }

  /**
   * Can be used in situations when object is reassined to the computed
   * new object, for example
   * obj = branch.apply(diff, obj)
   * can be converted to
   * obj = branch.applyInPlace(diff, obj)
   *
   * It clones makes cloning only for new objects creation
   */
  public applyInPlace(
    companyId: DataObjectId,
    id: DataObjectId,
    json: DataObjectJSON
  ): DataObjectJSON {
    if (this.companyId !== companyId) {
      logger.error('Incorrect branch companyId')
      return json
    }
    if (this.changes[id]) {
      return Diff.applyInPlace(
        this.changes[id],
        json as unknown as DeepReadonly<DataObjectJSON>
      ).result
    } else {
      return json
    }
  }

  /**
   * The order does matter.
   */
  static walkThroughChanges(
    branch: BranchJSON,
    cb: (objId: DataObjectId, diff: Diff) => void | boolean,
    limit = -1
  ): void {
    const t0 = Date.now()
    const keys = Object.keys(branch.changes)
    if (limit >= 0 && keys.length > limit) {
      logger.log(
        `Branch.walkThroughChanges ${keys.length} changes limit riched: ${limit}. Partially skipping`,
        branch.companyId
      )
      keys.splice(limit)
    }
    const updatesIds = keys.filter(
      id => !branch.changes[id].isNew
    ) as DataObjectId[]
    const creationsIds = keys.filter(
      id => branch.changes[id].isNew
    ) as DataObjectId[]
    const t1 = Date.now()
    if (t1 - t0 > 3000) {
      logger.log(
        `Long Branch.walkThroughChanges prepare ${t1 - t0} ms`,
        creationsIds.length,
        updatesIds.length,
        branch.companyId,
        new Error().stack
      )
    }
    let reportedTime = 0
    let count = 0
    let proceed = true
    creationsIds.every(id => {
      const t = Date.now() - t1
      count++
      if (t > 3000 && t - reportedTime > 3000) {
        reportedTime = t
        logger.log(
          `Long Branch.walkThroughChanges creations ${t} ms`,
          count,
          creationsIds.length,
          branch.companyId,
          new Error().stack
        )
      }
      const res = cb(id, branch.changes[id])
      if (res === false) {
        proceed = false
        return false
      }
      return true
    })
    if (!proceed || (limit >= 0 && count >= limit)) {
      return
    }
    updatesIds.every(id => {
      const t = Date.now() - t1
      count++
      if (t > 3000 && t - reportedTime > 3000) {
        reportedTime = t
        logger.log(
          `Long Branch.walkThroughChanges updates ${t} ms`,
          count,
          updatesIds.length,
          branch.companyId,
          new Error().stack
        )
      }
      const res = cb(id, branch.changes[id])
      const stopTheCycle = res === false || (limit >= 0 && count >= limit)
      return !stopTheCycle
    })
  }

  public static isEmpty(branch: BranchJSON): boolean {
    if (!branch?.changes) {
      return true
    }
    return Object.keys(branch.changes).length === 0
  }

  /**
   * Deprecated (support for minorVer < 3 clients)
   * Merge branches for sequence of revisions.
   */
  public static mergeBranches(branches: BranchJSON[]): BranchJSON {
    if (branches?.length === 1) {
      return Util.clone(branches[0])
    }
    const revs: Map<number, BranchJSON> = new Map()
    branches.forEach(b => {
      if (revs.has(b.fromRevision)) {
        logger.error('Branch merging: duplicate revision')
      }
      revs.set(b.fromRevision, Util.clone(b))
    })
    const revNumbers = [...revs.keys()].sort((a: number, b: number) => +b - +a)
    if (
      revNumbers[revNumbers.length - 1] - revNumbers[0] !==
      revNumbers.length - 1
    ) {
      logger.error(
        'Branch merge: given branches are not from sequential revisions',
        JSON.stringify(revNumbers)
      )
    }
    const first = revNumbers.shift()
    if (!first) {
      throw new TypeError('Branches list cannot be empty')
    }
    const res = revs.get(first)
    if (!res) {
      throw new Error('mergeBranches: revision not found')
    }
    res.id = Util.UUID()

    revNumbers.forEach((revision: number) => {
      const br = revs.get(revision)
      if (!br) {
        return
      }
      Branch.walkThroughChanges(br, (objId, diff) => {
        res.toRevision = br.toRevision
        if (res.companyId !== br.companyId) {
          logger.error('Branch merge: companyIds not match')
          return
        }
        res.changes[objId] = res.changes[objId]
          ? Diff.merge(res.changes[objId], diff)
          : diff
      })
    })
    return res
  }
}
